import { LoginResponse } from "@/service/auth.service";
import {
  loginService,
  logoutService,
  registerService,
  otpValidationService,
  resendOtpService,
  resetPasswordService,
  getSelfService,
  forgotPasswordService,
} from "@/service/auth.service";
import type { AxiosError } from "axios";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store";
import { NavigateFunction } from "react-router";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage"; // defaults to localStorage for web
import { ROLE_NAMES } from "@/utils/constants";
import { resetAllState as resetAssignmentState } from "./assignmentSlice";
import { resetAllState as resetStudentState } from "./studentSlice";
import { resetAllState as resetSchoolState } from "./schoolSlice";
import { resetAllState as resetTeacherState } from "./teacherSlice";
import { resetAllState as resetCourseState } from "./courseSlice";
import { resetAllState as resetFeedbackState } from "./feedbackSlice";
import { resetAllState as resetSubmissionState } from "./submissionSlice";
import { resetAllState as resetStudentLogState } from "./studentLogSlice";

interface User {
  id: string;
  token: string;
  username: string;
  role: {
    code: string;
    permissions: string[];
  };
  school_id: string;
  teacher_id: string;
  student_id: string;
  student_user_id: string;
  user_id: string;
  // expires_on: string;
  // first_name: string;
  // last_name: string;
  // email: string;
  // contact_number: string;
  name?: string;
  roleName?: string;
  school_list?: any[]; // Add school_list to User interface
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  // roles: GetAllRolesResponse[];
  geolocation: {
    latitude: number | null;
    longitude: number | null;
  };

  courseCount: {
    active: {
      general_assignment: string;
      unit_assignment: string;
      query: string;
    };
    total: {
      assignment: string;
      query: string;
    };
  };
}

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  // roles: [],
  geolocation: {
    latitude: null,
    longitude: null,
  },

  courseCount: {
    active: {
      general_assignment: "0",
      unit_assignment: "0",
      query: "1",
    },
    total: {
      assignment: "0",
      query: "1",
    },
  },
};

// Async Actions
export const login = createAsyncThunk(
  "auth/login",
  async (
    credentials: { email: string; password: string },
    { dispatch, getState }
  ) => {
    try {
      dispatch(setLoading(true));

      const state = getState() as RootState;

      const addInfo = {
        latitude: state.auth.geolocation.latitude || "",
        longitude: state.auth.geolocation.longitude || "",
      };

      const response = await loginService(
        credentials.email,
        credentials.password,
        addInfo
      );

      // Extract data from response
      const responseData = response.data as LoginResponse;

      // Create user object from response data
      const user: User = {
        id: responseData.data.token, // Using token as ID since we don't have a separate ID field
        token: responseData.data.token,
        username: responseData.data.username,
        role: responseData.data.roles[0],
        roleName:
          ROLE_NAMES[
          responseData.data.roles[0].code as keyof typeof ROLE_NAMES
          ] || "",
        school_id: responseData.data.school_id || "",
        teacher_id: responseData.data.teacher_id || "",
        student_id: responseData.data.student_id || "",
        student_user_id: responseData.data.user_id || "",
        user_id: responseData.data.user_id || "",
        name: responseData.data.name,
      };

      formSchoolList(responseData?.data?.schools, user);
      dispatch(loginSuccess(user));

      dispatch(refetchCourseCount());

      return user;
    } catch (error) {
      console.log(error);
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message || "Login failed";
      dispatch(setError(message));
      throw error;
    }
  }
);

const formSchoolList = (schools: any[] | undefined, user: User) => {
  if (schools && schools.length > 0) {
    user.school_list = [...[{ name: 'All', id: schools?.map((s) => s.id)?.join(',') || "", type: "all" }], ...schools];
    if (user?.school_list?.length > 0) {
      user.school_id = user?.school_list?.[0]?.id || ""; // Set school_id to the first school's ID for backward compatibility
    }
  }
  console.log("User object with school list:", user);
};

// Define the response type for forgot password
interface ForgotPasswordData {
  is_email_sent: boolean;
  id: string;
  sent: string;
}

// Forgot Password action
export const forgotPassword = createAsyncThunk<ForgotPasswordData, string>(
  "auth/forgotPassword",
  async (email: string, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      dispatch(clearError());

      const response = await forgotPasswordService(email);

      dispatch(setLoading(false));

      return response.data.data as ForgotPasswordData;
    } catch (error) {
      console.log(error);
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Forgot password request failed";
      console.log({ message });
      dispatch(setError(message));
      throw error;
    }
  }
);

// Register action
export const register = createAsyncThunk(
  "auth/register",
  async (credentials: { email: string }, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      dispatch(clearError());

      const { data } = await registerService(credentials.email);

      dispatch(setLoading(false));

      return data.data;
    } catch (error) {
      console.log(error);
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Registration failed";
      dispatch(setError(message));
      throw error;
    }
  }
);

// OTP Validation action
export const validateOtp = createAsyncThunk(
  "auth/validateOtp",
  async (credentials: { id: string; otp: string }, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      dispatch(clearError());

      const { data } = await otpValidationService(
        credentials.id,
        credentials.otp
      );

      console.log({ data });

      dispatch(setLoading(false));

      return data.data;
    } catch (error) {
      console.log(error);
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "OTP validation failed";
      dispatch(setError(message));
      throw error;
    }
  }
);

// Resend OTP action
export const resendOtp = createAsyncThunk(
  "auth/resendOtp",
  async (id: string, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      dispatch(clearError());

      const { data } = await resendOtpService(id);

      dispatch(setLoading(false));

      return data.data;
    } catch (error) {
      console.log(error);
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to resend OTP";
      dispatch(setError(message));
      throw error;
    }
  }
);

// Reset Password action
export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (credentials: { token: string; password: string }, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      dispatch(clearError());

      const response = await resetPasswordService(
        credentials.token,
        credentials.password
      );

      dispatch(setLoading(false));

      return response.data;
    } catch (error) {
      console.log(error);
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to reset password";
      dispatch(setError(message));
      throw error;
    }
  }
);

// Get Self action to refresh user data when page loads or user returns
export const refetchCourseCount = createAsyncThunk(
  "auth/getSelf",
  async (_, { dispatch }) => {
    try {
      console.log("refetchCourseCount response:");
      const response = await getSelfService();

      // Extract data from response
      const responseData = response.data as LoginResponse;

      if (responseData.data.counts) {
        dispatch(setCourseCount(responseData.data.counts));
      }
      return null;
    } catch (error) {
      console.log("Error fetching user data:", error);

      throw error;
    }
  }
);

// Get Self action to refresh user data when page loads or user returns
export const getSelf = createAsyncThunk(
  "auth/getSelf",
  async (_, { dispatch, getState }) => {
    try {
      // Check if user is already authenticated
      const state = getState() as RootState;
      if (!state.auth.isAuthenticated || !state.auth.token) {
        // No need to make the API call if user is not authenticated
        return null;
      }

      console.log("getSelf called");

      const response = await getSelfService();

      // Extract data from response
      const responseData = response.data as LoginResponse;

      // Create user object from response data
      const user: User = {
        id: responseData.data.token, // Using token as ID since we don't have a separate ID field
        token: responseData.data.token,
        username: responseData.data.username,
        role: responseData.data.roles[0],
        roleName:
          ROLE_NAMES[
          responseData.data.roles[0].code as keyof typeof ROLE_NAMES
          ] || "",
        school_id: responseData.data.school_id || "",
        teacher_id: responseData.data.teacher_id || "",
        student_id: responseData.data.student_id || "",
        student_user_id: responseData.data.user_id || "",
        user_id: responseData.data.user_id || "",
        name: responseData.data.name,
      };

      formSchoolList(responseData?.data?.schools, user);
      dispatch(loginSuccess(user));

      if (responseData.data.counts) {
        dispatch(setCourseCount(responseData.data.counts));
      }
      return null;
    } catch (error) {
      console.log("Error fetching user data:", error);
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to fetch user data";

      // If we get an authentication error, log the user out
      if (axiosError.response?.status === 401) {
        dispatch(logoutSuccess());
      } else {
        dispatch(setError(message));
      }

      throw error;
    }
  }
);

// Logout action
export const logout = createAsyncThunk(
  "auth/logout",
  async (navigate: NavigateFunction, { dispatch }) => {
    try {
      dispatch(setLoading(true));

      // Call the logout service
      await logoutService();

      // Dispatch logout success action which will trigger the rootReducer to clear all state
      dispatch(logoutSuccess());

      // Reset all state
      dispatch(resetAssignmentState());
      dispatch(resetStudentState());
      dispatch(resetSchoolState());
      dispatch(resetTeacherState());
      dispatch(resetCourseState());
      dispatch(resetFeedbackState());
      dispatch(resetSubmissionState());
      dispatch(resetStudentLogState());

      // Force a redirect to sign in page
      setTimeout(() => {
        navigate("/auth/signin");
      }, 100);

      return true;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message || "Logout failed";
      dispatch(setError(message));
      throw error;
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setCourseCount: (
      state,
      action: PayloadAction<AuthState["courseCount"]>
    ) => {
      state.courseCount = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    loginSuccess: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.loading = false;
      state.error = null;
    },
    logoutSuccess: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
    },
    updateDisplayName: (
      state,
      action: PayloadAction<{ first_name: string; last_name: string }>
    ) => {
      state.user = {
        ...state.user,
        name: action.payload.first_name + " " + action.payload.last_name,
      } as User;
    },
    updateSchool: (
      state,
      action: PayloadAction<{ school_id: string }>
    ) => {
      state.user = {
        ...state.user,
        school_id: action?.payload?.school_id, // Update school_id in the user object
      } as any;
    },
    setGeolocation: (
      state,
      action: PayloadAction<{ latitude: number; longitude: number }>
    ) => {
      state.geolocation = action.payload;
    },
  },
});

export const {
  setLoading,
  setError,
  clearError,
  loginSuccess,
  logoutSuccess,
  updateDisplayName,
  updateSchool,
  setGeolocation,
  setCourseCount,
} = authSlice.actions;

// Selectors
export const selectAuth = (state: RootState) => state.auth;
export const selectUser = (state: RootState) => state.auth.user;
export const selectIsAuthenticated = (state: RootState) =>
  state.auth.isAuthenticated;
export const selectAuthLoading = (state: RootState) => state.auth.loading;
export const selectAuthError = (state: RootState) => state.auth.error;
export const selectToken = (state: RootState) => state.auth.token;
export const selectUserRole = (state: RootState) => state.auth.user?.role;
export const selectGeolocation = (state: RootState) => state.auth.geolocation;

export const selectCourseCount = (state: RootState) => state.auth.courseCount;
export const selectSchoolId = (state: RootState) => state.auth.user?.school_id;
export const isAllSchoolsSelected = (state: RootState) =>
  (state?.auth?.user?.school_id?.split(",")?.length ?? 0) > 1;

const persistConfig = {
  key: "auth",
  storage,
  whitelist: ["token", "isAuthenticated", "user"], // only persist these fields
};

export default persistReducer(persistConfig, authSlice.reducer);
